'use client';

import { useEffect, useState, useRef, useCallback } from 'react';
import { io, Socket } from 'socket.io-client';

export const useSocket = (url: string, token?: string) => {
  const [isConnected, setIsConnected] = useState(false);
  const socketRef = useRef<Socket | null>(null);
  const reconnectAttempts = useRef(0);
  const maxReconnectAttempts = 5;
  const pendingAcks = useRef<Map<string, NodeJS.Timeout>>(new Map());

  useEffect(() => {
    let reconnectTimer: NodeJS.Timeout;

    const connectSocket = () => {
      if (reconnectAttempts.current >= maxReconnectAttempts) {
        console.warn('Max reconnection attempts reached');
        return;
      }

      // Create socket connection with optimized config
      const socket = io(url, {
        transports: ['websocket'],
        path: '/socket.io/',
        reconnection: true,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000,
        reconnectionAttempts: 5,
        timeout: 20000,
        autoConnect: true,
        auth: token ? { token } : undefined,
      });

      // Set up event listeners
      socket.on('connect', () => {
        console.log('WebSocket connected');
        setIsConnected(true);
        reconnectAttempts.current = 0; // Reset attempts on successful connection
      });

      socket.on('disconnect', () => {
        console.log('WebSocket disconnected');
        setIsConnected(false);

        // Clear all pending acknowledgments on disconnect
        pendingAcks.current.forEach((timeoutId) => {
          clearTimeout(timeoutId);
        });
        pendingAcks.current.clear();
      });

      socket.on('connect_error', (err) => {
        console.error('Connection error:', err);
        setIsConnected(false);
        reconnectAttempts.current++;

        // Try to reconnect after delay with exponential backoff
        if (reconnectAttempts.current < maxReconnectAttempts) {
          const delay = Math.min(2000 * Math.pow(1.5, reconnectAttempts.current - 1), 10000);
          console.log(`Attempting reconnect ${reconnectAttempts.current}/${maxReconnectAttempts} in ${delay}ms`);

          reconnectTimer = setTimeout(() => {
            socket.connect();
          }, delay);
        }
      });

      socket.onAny((event, data) => {
        console.log('[Socket] Event:', event, 'Data:', data);
      });

      // Store socket instance
      socketRef.current = socket;
    };

    connectSocket();

    // Clean up on unmount
    return () => {
      if (reconnectTimer) {
        clearTimeout(reconnectTimer);
      }

      // Clear all pending acknowledgments
      pendingAcks.current.forEach((timeoutId) => {
        clearTimeout(timeoutId);
      });
      pendingAcks.current.clear();

      if (socketRef.current) {
        socketRef.current.removeAllListeners();
        socketRef.current.close();
        socketRef.current = null;
      }
    };
  }, [url, token, maxReconnectAttempts]);

  // Function to emit events with timeout for critical events
  const emit = useCallback((event: string, data: any, callback?: (response: any) => void) => {
    if (socketRef.current?.connected) {
      if (callback) {
        // Generate a unique ID for this ack
        const ackId = `${event}-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;

        // Set timeout for acknowledgment
        const timeoutId = setTimeout(() => {
          console.warn(`Socket event ${event} acknowledgment timed out`);
          // Remove from pending acks
          pendingAcks.current.delete(ackId);
          // Call callback with timeout error
          callback({ error: 'timeout', message: 'Socket event acknowledgment timed out' });
        }, 15000); // 15 second timeout

        // Store timeout ID
        pendingAcks.current.set(ackId, timeoutId);

        // Emit with wrapped callback that clears timeout
        socketRef.current.emit(event, data, (response: any) => {
          // Clear timeout
          clearTimeout(pendingAcks.current.get(ackId));
          pendingAcks.current.delete(ackId);
          // Call original callback
          callback(response);
        });
      } else {
        socketRef.current.emit(event, data);
      }
    } else {
      console.warn(`Cannot emit event: socket not connected (event: ${event})`);
      // If callback is provided, call it with connection error
      if (callback) {
        callback({ error: 'connection', message: 'Socket not connected' });
      }
    }
  }, []);

  // Function to subscribe to events
  const on = useCallback((event: string, callback: (data: any) => void) => {
    if (socketRef.current) {
      socketRef.current.on(event, callback);
    } else {
      console.warn(`Cannot subscribe to event: socket not initialized (event: ${event})`);
    }

    // Return unsubscribe function
    return () => {
      if (socketRef.current) {
        socketRef.current.off(event, callback);
      }
    };
  }, []);

  return { isConnected, emit, on };
}; 